import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

export default function FranchiseLogin() {
    const navi = useNavigate();
    const auth = Cookies.get('authuser');

    const api = process.env.REACT_APP_API

    useEffect(() => {
        if (auth === 'franchise') {
            navi('/controller/dashboard-franchise', { replace: true });
        }
    }, [auth]);

    const [step, setStep] = useState('email'); // 'email' | 'otp'
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [loading, setLoading] = useState(false);

    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [otpError, setOtpError] = useState('');
    const [shake, setShake] = useState(false);
    const otpRefs = useRef([]);

    const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

    const triggerShake = () => {
        setShake(true);
        setTimeout(() => setShake(false), 500);
    };

    // Step 1 — Send OTP
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            setEmailError('Email address is required.');
            triggerShake();
            return;
        }
        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address.');
            triggerShake();
            return;
        }
        setEmailError('');
        setLoading(true);
        try {
            const res = await fetch(api + '/franchise/login/send-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await res.json();
            if (data.status === false) {
                setEmailError(data.message || 'Failed to send OTP.');
                triggerShake();
            } else {
                setStep('otp');
                setTimeout(() => otpRefs.current[0]?.focus(), 150);
            }
        } catch {
            setEmailError('Network error. Please try again.');
            triggerShake();
        } finally {
            setLoading(false);
        }
    };

    // Step 2 — Verify OTP & Sign in
    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        const code = otp.join('');
        if (code.length < 6) {
            setOtpError('Please enter the complete 6-digit OTP.');
            triggerShake();
            return;
        }
        setOtpError('');
        setLoading(true);
        try {
            const res = await fetch(api + '/franchise/login/signin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, otp: code }),
            });
            const data = await res.json();
            if (data.status === false) {
                setOtpError(data.message || 'Invalid OTP. Please try again.');
                triggerShake();
            } else {
                Cookies.set('franchise_token', data.data.token, { expires: 7 });
                Cookies.set('franchise_role', data.data.role, { expires: 7 });
                Cookies.set('authuser', 'franchise', { expires: 7 });
                navi('/controller/dashboard-franchise', { replace: true });
            }
        } catch {
            setOtpError('Network error. Please try again.');
            triggerShake();
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const updated = [...otp];
        updated[index] = value.slice(-1);
        setOtp(updated);
        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 6) {
            setOtp(pasted.split(''));
            otpRefs.current[5]?.focus();
        }
        e.preventDefault();
    };

    const handleResendOtp = async () => {
        setOtp(['', '', '', '', '', '']);
        setOtpError('');
        setLoading(true);
        try {
            const res = await fetch(api + '/franchise/login/send-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await res.json();
            if (data.status === false) {
                setOtpError(data.message || 'Failed to resend OTP.');
            } else {
                setTimeout(() => otpRefs.current[0]?.focus(), 100);
            }
        } catch {
            setOtpError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                .fl-root {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-family: 'Inter', sans-serif;
                    background: #f5f5f5;
                    padding: 20px;
                }

                /* Card */
                .fl-card {
                    width: 100%;
                    max-width: 420px;
                    background: #fff;
                    border: 1px solid #e8ecef;
                    border-radius: 16px;
                    padding: 44px 40px 48px;
                    animation: cardIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) both;
                }
                @keyframes cardIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                /* Logo */
                .fl-logo {
                    text-align: center;
                    margin-bottom: 32px;
                }
                .fl-logo-name {
                    display: block;
                    font-size: 18px; font-weight: 800; letter-spacing: 2px;
                    color: #0E3A43;
                }
                .fl-logo-sub {
                    display: block;
                    font-size: 11px; font-weight: 500; letter-spacing: 2px;
                    color: #9eadb0; text-transform: uppercase; margin-top: 3px;
                }

                /* Steps */
                .fl-steps {
                    display: flex; align-items: center; justify-content: center;
                    gap: 6px; margin-bottom: 28px;
                }
                .fl-step-dot {
                    width: 6px; height: 6px; border-radius: 50%;
                    background: #dde3e4;
                    transition: background 0.3s, transform 0.3s;
                }
                .fl-step-dot.active { background: #0E3A43; transform: scale(1.4); }
                .fl-step-dot.done   { background: #1a5f6a; }

                /* Headings */
                .fl-title {
                    font-size: 22px; font-weight: 800; color: #0E3A43;
                    text-align: center; margin-bottom: 6px;
                }
                .fl-subtitle {
                    font-size: 13px; color: #7a9298;
                    text-align: center; margin-bottom: 30px; line-height: 1.6;
                }
                .fl-subtitle strong { color: #0E3A43; font-weight: 600; }

                /* Input */
                .fl-field { margin-bottom: 8px; }
                .fl-label {
                    display: block;
                    font-size: 11.5px; font-weight: 600; letter-spacing: 0.7px;
                    color: #7a9298; text-transform: uppercase; margin-bottom: 7px;
                }
                .fl-input {
                    width: 100%;
                    background: #f7f9fa;
                    border: 1.5px solid #dde3e4;
                    border-radius: 10px;
                    padding: 13px 16px;
                    font-size: 15px; font-family: 'Inter', sans-serif;
                    color: #0E3A43;
                    outline: none;
                    transition: border-color 0.2s, background 0.2s;
                }
                .fl-input::placeholder { color: #b5c4c7; }
                .fl-input:focus {
                    border-color: #0E3A43;
                    background: #fff;
                }
                .fl-input.error { border-color: #ef5350; }

                /* Error */
                .fl-error {
                    font-size: 12px; color: #ef5350; margin-top: 5px;
                    display: flex; align-items: center; gap: 4px;
                    min-height: 18px;
                    animation: fadeIn 0.2s ease;
                }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: translateY(0); } }

                /* Shake */
                .fl-shake { animation: shake 0.45s cubic-bezier(.36,.07,.19,.97) both; }
                @keyframes shake {
                    10%, 90% { transform: translateX(-2px); }
                    20%, 80% { transform: translateX(4px); }
                    30%, 50%, 70% { transform: translateX(-5px); }
                    40%, 60% { transform: translateX(5px); }
                }

                /* Button */
                .fl-btn {
                    width: 100%; margin-top: 22px;
                    padding: 14px;
                    background: #0E3A43;
                    border: none; border-radius: 10px;
                    font-size: 14.5px; font-weight: 700;
                    color: #fff; cursor: pointer;
                    transition: background 0.2s, transform 0.15s;
                    letter-spacing: 0.3px;
                }
                .fl-btn:hover:not(:disabled) { background: #1a5f6a; transform: translateY(-1px); }
                .fl-btn:active:not(:disabled) { transform: translateY(0); }
                .fl-btn:disabled { opacity: 0.6; cursor: not-allowed; }

                /* OTP */
                .fl-otp-grid {
                    display: flex; gap: 5px; justify-content: center; margin-bottom: 8px;
                }
                .fl-otp-box {
                    width: 40px; height: 48px;
                    background: #f7f9fa;
                    border: 1.5px solid #dde3e4;
                    border-radius: 10px;
                    text-align: center;
                    font-size: 20px; font-weight: 700;
                    color: #0E3A43; font-family: 'Inter', sans-serif;
                    outline: none;
                    transition: border-color 0.2s, background 0.2s;
                    caret-color: #0E3A43;
                }
                .fl-otp-box:focus {
                    border-color: #0E3A43;
                    background: #fff;
                }
                .fl-otp-box.filled { border-color: #1a5f6a; background: #f0fafb; }
                .fl-otp-box.error  { border-color: #ef5350; }

                /* Resend / Back */
                .fl-resend {
                    text-align: center; margin-top: 18px;
                    font-size: 13px; color: #9eadb0;
                }
                .fl-resend-btn {
                    background: none; border: none; font-family: 'Inter', sans-serif;
                    font-size: 13px; color: #0E3A43; cursor: pointer; font-weight: 600;
                    transition: color 0.2s;
                }
                .fl-resend-btn:hover:not(:disabled) { color: #1a5f6a; }
                .fl-resend-btn:disabled { opacity: 0.5; cursor: not-allowed; }

                .fl-back {
                    display: flex; align-items: center; justify-content: center; gap: 5px;
                    margin-top: 20px;
                    font-size: 13px; color: #9eadb0; cursor: pointer;
                    background: none; border: none; font-family: 'Inter', sans-serif;
                    transition: color 0.2s;
                }
                .fl-back:hover { color: #0E3A43; }

                /* Panel slide */
                .fl-panel {
                    animation: panelIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
                }
                @keyframes panelIn {
                    from { opacity: 0; transform: translateX(20px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                .fl-panel-back {
                    animation: panelBack 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
                }
                @keyframes panelBack {
                    from { opacity: 0; transform: translateX(-20px); }
                    to   { opacity: 1; transform: translateX(0); }
                }

                /* Spinner */
                .fl-spinner {
                    display: inline-block; width: 14px; height: 14px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff;
                    border-radius: 50%;
                    animation: spin 0.65s linear infinite;
                    vertical-align: middle; margin-right: 6px;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>

            <div className="fl-root">
                <div className={`fl-card ${shake ? 'fl-shake' : ''}`}>

                    {/* Brand */}
                    <div className="fl-logo">
                        <span className="fl-logo-name">SPYWARE COMPUTER EDUCATION</span>
                        <span style={{ color: 'gray', fontSize: 10 }}>Welcome! Login to your franchise account</span>
                    </div>

                    {/* Step dots */}
                    <div className="fl-steps">
                        <div className={`fl-step-dot ${step === 'email' ? 'active' : 'done'}`} />
                        <div className={`fl-step-dot ${step === 'otp' ? 'active' : ''}`} />
                    </div>

                    {/* ── EMAIL STEP ── */}
                    {step === 'email' && (
                        <div className="fl-panel-back">
                            <div className="fl-title">Franchise Login</div>
                            <div className="fl-subtitle">Enter your registered email to receive an OTP</div>

                            <form onSubmit={handleEmailSubmit} noValidate>
                                <div className="fl-field">
                                    <label className="fl-label" htmlFor="fl-email">Email Address</label>
                                    <input
                                        id="fl-email"
                                        className={`fl-input ${emailError ? 'error' : ''}`}
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                                        autoComplete="email"
                                        autoFocus
                                        disabled={loading}
                                    />
                                    <div className="fl-error">
                                        {emailError && <><span>⚠</span>{emailError}</>}
                                    </div>
                                </div>

                                <button type="submit" className="fl-btn" disabled={loading}>
                                    {loading ? <><span className="fl-spinner" />Sending OTP…</> : 'Continue →'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* ── OTP STEP ── */}
                    {step === 'otp' && (
                        <div className="fl-panel">
                            <div className="fl-title">Verify OTP</div>
                            <div className="fl-subtitle">
                                We sent a 6-digit code to<br />
                                <strong>{email}</strong>
                            </div>

                            <form onSubmit={handleOtpSubmit} noValidate>
                                <label className="fl-label" style={{ textAlign: 'center', display: 'block', marginBottom: 12 }}>
                                    Enter OTP
                                </label>
                                <div className="fl-otp-grid" onPaste={handleOtpPaste}>
                                    {otp.map((digit, i) => (
                                        <input
                                            key={i}
                                            ref={el => otpRefs.current[i] = el}
                                            className={`fl-otp-box ${otpError ? 'error' : ''} ${digit ? 'filled' : ''}`}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={1}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(i, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                            id={`fl-otp-${i}`}
                                            disabled={loading}
                                        />
                                    ))}
                                </div>
                                <div className="fl-error" style={{ justifyContent: 'center' }}>
                                    {otpError && <><span>⚠</span>{otpError}</>}
                                </div>

                                <button type="submit" className="fl-btn" disabled={loading}>
                                    {loading ? <><span className="fl-spinner" />Verifying…</> : 'Verify & Login'}
                                </button>
                            </form>

                            <div className="fl-resend">
                                Didn't receive it?&nbsp;
                                <button
                                    className="fl-resend-btn"
                                    type="button"
                                    onClick={handleResendOtp}
                                    disabled={loading}
                                >
                                    Resend OTP
                                </button>
                            </div>

                            <button
                                className="fl-back"
                                type="button"
                                disabled={loading}
                                onClick={() => { setStep('email'); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
                            >
                                ← Change email
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
}